import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { useHistory } from "react-router-dom";

import { Formik, FormikValues } from "formik";
import { bindActionCreators, Dispatch } from "redux";

import { ActionBar, CancelLink, Form, OkButton, Overlay, ColumnType } from "@plex/react-components";

import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { useLocalizedValues, useUserContext } from "ui/hooks";

import { releaseMessages } from "../release.localization";

import ReleaseId from "./components/ReleaseId";
import ReleaseMessages from "./components/ReleaseMessages";
import ReleaseTerms from "./components/ReleaseTerms";
import { releaseDetailsManagerActions } from "./ReleaseDetails.actions";
import styles from "./ReleaseDetails.module.scss";
import { IMessageData, IReleaseDetailsState, ITermData, Status } from "./ReleaseDetails.state";

interface IReleaseDetailsProps {
  isLoading: boolean;
  shouldRedirect: boolean | undefined;
  notifications: INotification[];
  status: string;
  selectedAssociatedTerms: ITermData[];
  selectedMessages: IMessageData[];
  freezeRelease: (releaseId: string) => void;
  unfreezeRelease: (releaseId: string) => void;
  shipRelease: (releaseId: string) => void;
  cancelLinkClick: () => void;
  deleteTermsFromGrid: () => void;
  deleteMessagesFromGrid: () => void;
  loadReleaseData: (releaseId: string) => void;
  updateRelease: (releaseId: string) => void;
}

const ReleaseDetails: React.FC<IReleaseDetailsProps & RouteComponentProps> = ({
  isLoading,
  notifications,
  match,
  shouldRedirect,
  status,
  selectedAssociatedTerms,
  selectedMessages,
  freezeRelease,
  unfreezeRelease,
  shipRelease,
  cancelLinkClick,
  loadReleaseData,
  deleteTermsFromGrid,
  deleteMessagesFromGrid,
  updateRelease
}) => {
  const releaseId = match.params["releaseId"];
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();
  const history = useHistory();
  const doubleColumnLayoutMinWidth = 1400;
  const [formColumnLayout, setFormColumnLayout] = useState<ColumnType>(ColumnType.double);

  useEffect(() => {
    releaseId && loadReleaseData(releaseId);
    const resizeWindowHandler = e => setFormColumnLayout(e.target?.innerWidth < doubleColumnLayoutMinWidth ? ColumnType.single : ColumnType.double);
    window.addEventListener("resize", resizeWindowHandler);

    return () => window.removeEventListener("resize", resizeWindowHandler);
  }, [releaseId, loadReleaseData]);

  const localizedValues = useLocalizedValues(() => ({
    header: releaseMessages.title("releaseDetails"),
    freezeButtonText: releaseMessages.action("freezeRelease"),
    unfreezeButtonText: releaseMessages.action("unfreezeRelease"),
    shipButtonText: releaseMessages.action("shipRelease"),
    termDeleteButtonText: releaseMessages.action("termDelete"),
    messageDeleteButtonText: releaseMessages.action("messageDelete"),
    releaseTermsSectionTitle: releaseMessages.heading("releaseTerms"),
    manageMessagesLabel: releaseMessages.heading("releaseMessages")
  }));

  useEffect(() => {
    if (shouldRedirect) {
      // TODO: for some reason the releases grid is not in the history after you click into a release. figure out why
      history.push("/releases");
      history.go(0);
    }
  }, [shouldRedirect, history]);

  const onFreezeClick = useCallback(() => {
    freezeRelease(releaseId);
  }, [freezeRelease, releaseId]);

  const onUnfreezeClick = useCallback(() => {
    unfreezeRelease(releaseId);
  }, [unfreezeRelease, releaseId]);

  const onShipClick = useCallback(() => {
    shipRelease(releaseId);
  }, [shipRelease, releaseId]);

  const onDeleteTermClick = useCallback(() => {
    deleteTermsFromGrid();
  }, [deleteTermsFromGrid]);

  const onDeleteMessageClick = useCallback(() => {
    deleteMessagesFromGrid();
  }, [deleteMessagesFromGrid]);
  const disableFreezeButton = useMemo(() => {
    return status === releaseMessages.option("releaseStatusFrozen").value || status === releaseMessages.option("releaseStatusShipped").value;
  }, [status]);

  const disableUnfreezeButton = useMemo(() => {
    return status === releaseMessages.option("releaseStatusPlanning").value || status === releaseMessages.option("releaseStatusShipped").value;
  }, [status]);

  const disableShipButton = useMemo(() => {
    return status === releaseMessages.option("releaseStatusShipped").value || status === releaseMessages.option("releaseStatusPlanning").value;
  }, [status]);

  const disableDeleteTermButton = useMemo(() => {
    return selectedAssociatedTerms.length === 0 || selectedAssociatedTerms.some(x => x.status === Status.Deleted);
  }, [selectedAssociatedTerms]);

  const disableMessagesTermButton = useMemo(() => {
    return selectedMessages.length === 0 || selectedMessages.some(x => x.status === Status.Deleted);
  }, [selectedMessages]);

  const onSubmitClick = useCallback(
    (handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void) => {
      handleSubmit();
      updateRelease(releaseId);
    },
    [updateRelease, releaseId]
  );

  return (
    <>
      <Overlay show={isLoading} showSpinner />
      {releaseId && roles.PlexReleaseManager ? (
        <ActionBar>
          <ActionBar.Button
            id="freezeButton"
            text={localizedValues.freezeButtonText}
            title={localizedValues.freezeButtonText}
            disabled={disableFreezeButton}
            onClick={onFreezeClick}
          />
          <ActionBar.Button
            id="unfreezeButton"
            text={localizedValues.unfreezeButtonText}
            title={localizedValues.unfreezeButtonText}
            disabled={disableUnfreezeButton}
            onClick={onUnfreezeClick}
          />
          <ActionBar.Button id="shipButton" text={localizedValues.shipButtonText} title={localizedValues.shipButtonText} disabled={disableShipButton} onClick={onShipClick} />
          <ActionBar.Button
            id="deleteTermButton"
            text={localizedValues.termDeleteButtonText}
            title={localizedValues.termDeleteButtonText}
            disabled={disableDeleteTermButton}
            onClick={onDeleteTermClick}
          />
          <ActionBar.Button
            id="deleteMessageButton"
            text={localizedValues.messageDeleteButtonText}
            title={localizedValues.messageDeleteButtonText}
            disabled={disableMessagesTermButton}
            onClick={onDeleteMessageClick}
          />
        </ActionBar>
      ) : (
        <ActionBar />
      )}
      <Formik initialValues={{}} onSubmit={(_: FormikValues) => {}}>
        {({ handleSubmit }) => (
          <Form className={styles.formContainer}>
            <Form.Header>
              <Banner messages={notifications} />
            </Form.Header>
            <Form.Body>
              <Form.Section layout={formColumnLayout} title={localizedValues.header}>
                <ReleaseId releaseIdParameter={releaseId}></ReleaseId>
              </Form.Section>
              <Form.Section title={localizedValues.releaseTermsSectionTitle}>
                <ReleaseTerms></ReleaseTerms>
              </Form.Section>
              <Form.Section title={localizedValues.manageMessagesLabel}>
                <ReleaseMessages></ReleaseMessages>
              </Form.Section>
            </Form.Body>
            <Form.Footer className={styles.formFooter}>
              <CancelLink onClick={cancelLinkClick} />
              {roles.PlexReleaseManager && <OkButton onClick={() => onSubmitClick(handleSubmit)} />}
            </Form.Footer>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default connect(
  (state: { releaseDetails: IReleaseDetailsState }) => {
    return {
      shouldRedirect: state.releaseDetails.shouldRedirect,
      notifications: state.releaseDetails.notifications,
      status: state.releaseDetails.releaseStatus,
      selectedAssociatedTerms: state.releaseDetails.selectedAssociatedTerms,
      selectedMessages: state.releaseDetails.selectedAssociatedMessages,
      isLoading: state.releaseDetails.isLoading
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        loadReleaseData: releaseDetailsManagerActions.loadReleaseData,
        freezeRelease: releaseDetailsManagerActions.freezeRelease,
        unfreezeRelease: releaseDetailsManagerActions.unfreezeRelease,
        shipRelease: releaseDetailsManagerActions.shipRelease,
        cancelLinkClick: releaseDetailsManagerActions.cancelLinkClick,
        deleteTermsFromGrid: releaseDetailsManagerActions.deleteTermsFromGrid,
        deleteMessagesFromGrid: releaseDetailsManagerActions.deleteMessagesFromGrid,
        updateRelease: releaseDetailsManagerActions.updateRelease
      },
      dispatch
    )
)(ReleaseDetails);
